/**
 * QuestCardRow — collapsed-строка карточки квеста (эталон js:195-227):
 * qtop (marquee-имя, бейдж short, цена «₽X до N + ₽Y», у открытой — кнопка
 * «развернуть всё», ✕) + qmeta (Игра/Пауза/мини-операторы/диапазон слотов)
 * + qlinks (связи short-имён через « · » + чипы спецверсий с Δ).
 */
import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import type { Quest } from "../../api/types";
import type { QuestsAdmin } from "../../hooks/useQuestsAdmin";
import { money } from "./EoConfigTeas";
import { OpsMini, opsOfQuest } from "./OpsRow";
import { fmtD } from "./QuestFormCells";
import { CrossIco, DoubleChevIco, LinkIco, SparkIco } from "./questIcons";
import { keyOf } from "./slotTime";
import css from "./quests.module.css";

/** Имя с marquee при переполнении (эталон js:223). */
function QName({ name }: { name: string }) {
  const wrap = useRef<HTMLSpanElement>(null);
  const [mq, setMq] = useState(0);
  useLayoutEffect(() => {
    const w = wrap.current;
    const sp = w?.firstElementChild as HTMLElement | null;
    if (!w || !sp) return;
    setMq(sp.scrollWidth > w.clientWidth + 2 ? sp.scrollWidth - w.clientWidth + 6 : 0);
  }, [name]);
  return (
    <span
      ref={wrap}
      className={`${css.qname} ${mq ? css.marq : ""}`}
      style={mq ? ({ "--mq": `-${mq}px` } as CSSProperties) : undefined}
    >
      <span className={css.mq}>{name}</span>
    </span>
  );
}

export interface QuestCardRowProps {
  q: Quest;
  qa: QuestsAdmin;
  open: boolean;
  allOpen: boolean;
  links: string[];
  grid: string[];
  onExpAll: () => void;
  onDel: () => void;
}

export function QuestCardRow({ q, qa, open, allOpen, links, grid, onExpAll, onDel }: QuestCardRowProps) {
  const price = qa.prices[q.id];
  const specs = qa.versions.filter((v) => v.slot_quest_id === q.id);
  const sorted = [...grid].sort((a, b) => keyOf(a) - keyOf(b));
  const shortOf = (id: string) => qa.quests.find((x) => x.id === id)?.short_name ?? id;
  return (
    <>
      <div className={css.qtop}>
        <QName name={q.name} />
        <span className={css.qshort}>{q.short_name}</span>
        {price?.base_price ? (
          <span className={`${css.m} ${css.mp}`}>
            <span className={css.num}>{money(price.base_price)}</span> до{" "}
            <span className={css.num}>{price.threshold}</span> +{" "}
            <span className={css.num}>{money(price.extra_per_player)}</span>
          </span>
        ) : null}
        {open ? (
          <button
            type="button"
            className={`${css.tx} ${css.qexp} ${allOpen ? css.flip : ""}`}
            title={allOpen ? "Свернуть всё" : "Развернуть всё"}
            onClick={(e) => { e.stopPropagation(); onExpAll(); }}
          >
            <DoubleChevIco />
          </button>
        ) : null}
        <button
          type="button"
          className={`${css.tx} ${css.qdel}`}
          title="Удалить"
          onClick={(e) => { e.stopPropagation(); onDel(); }}
        >
          <CrossIco />
        </button>
      </div>
      <div className={css.qmeta}>
        <span className={`${css.m} ${css.mc}`}>Игра: <span className={css.num}>{q.duration}м</span></span>
        <span className={`${css.m} ${css.mo}`}>Пауза: <span className={css.num}>{q.reset_time}м</span></span>
        <span className={css.m}><OpsMini ops={opsOfQuest(q)} /></span>
        {sorted.length ? (
          <span className={`${css.m} ${css.mg}`}>
            Слоты: <span className={css.num}>{sorted[0]}—{sorted[sorted.length - 1]}</span>{" "}
            (<span className={css.num}>{grid.length}</span>)
          </span>
        ) : (
          <span className={`${css.m} ${css.md}`}>нет слотов</span>
        )}
      </div>
      {links.length || specs.length ? (
        <div className={css.qlinks}>
          {links.length ? (
            <><span className={css.linkIco}><LinkIco /></span>{links.map(shortOf).join(" · ")}</>
          ) : null}
          {specs.length ? (
            <span className={css.qspecs}>
              <span className={css.sparkIco}><SparkIco /></span>
              {specs.map((v) => (
                <span key={v.id} className={css.qsv}>
                  {v.title}
                  <span className={`${css.sdelta} ${css.num}`}>{fmtD((v.duration_min ?? q.duration) - q.duration)}</span>
                </span>
              ))}
            </span>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
